import React, { useEffect, useRef, useState } from "react";
import lottie from "lottie-web";
import moment from "moment";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
} from "reactstrap";
import Dropzone from "react-dropzone";
import SweetAlert from "react-bootstrap-sweetalert";
import { CircularProgress } from "@material-ui/core";
import { FaCloudUploadAlt, FaRegTrashAlt } from "react-icons/fa";

const animationInitialState = { data: null, name: "", deleted: false, changed: false };

const LottiePreview = ({ animationData }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const animation = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: JSON.parse(JSON.stringify(animationData)),
    });

    return () => animation.destroy();
  }, [animationData]);

  return <div ref={containerRef} />;
};

const LoadingScreenLogo = () => {
  const [token, setToken] = useState("");
  const [assetId, setAssetId] = useState(null);
  const [animationImage, setAnimationImage] = useState(animationInitialState);
  const [publishDate, setPublishDate] = useState("");
  const [unpublishDate, setUnpublishDate] = useState("");
  const [neverEnding, setNeverEnding] = useState(false);
  const [scheduleChanged, setScheduleChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const authenticationToken = localStorage.getItem("AuthenticationToken");
    setToken(authenticationToken || "");
    loadAssets(authenticationToken);
  }, []);

  const showError = (message) => setErrorMessage(message);

  const getAnimationData = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const loadAssets = (authenticationToken) => {
    const headers = new Headers();
    headers.append("Authorization", "Bearer " + authenticationToken);

    fetch(`${process.env.REACT_APP_TOKRI_API_URL}/api/app-customization-assets`, {
      method: "GET",
      headers,
    })
      .then((response) => response.json())
      .then((result) => {
        const data = Array.isArray(result?.data) ? result.data[0] : result?.data || result;
        if (!data) return;

        setAssetId(data.id || null);
        if (data.animation_image) {
          const animationData = getAnimationData(data.animation_image);
          if (animationData) {
            setAnimationImage({ data: animationData, name: "Current animation", deleted: false, changed: false });
          }
        }
        if (data.publish_date) {
          setPublishDate(moment(data.publish_date).format("YYYY-MM-DDTHH:mm"));
        }
        if (data.unpublish_date == null || data.unpublish_date === "null" || !data.unpublish_date) {
          setNeverEnding(true);
        } else {
          setUnpublishDate(moment(data.unpublish_date).format("YYYY-MM-DDTHH:mm"));
        }
      })
      .catch(() => showError("Unable to load Loading Screen Logo."))
      .finally(() => setLoading(false));
  };

  const selectAnimation = (file) => {
    if (file.type !== "application/json" && !file.name.toLowerCase().endsWith(".json")) {
      showError("Loading Screen Logo must be a Lottie JSON file.");
      return;
    }
    if (file.size >= 150 * 1024) {
      showError("Loading Screen Logo must be smaller than 150KB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (data.w !== 497 || data.h !== 120) {
          showError("Loading Screen Logo must be 497 x 120px.");
          return;
        }
        setAnimationImage({ data, name: file.name, deleted: false, changed: true });
      } catch (error) {
        showError("Please select a valid Lottie JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = () => {
    const hasChanges = animationImage.changed || scheduleChanged;
    if (!hasChanges) {
      showError("Please upload or change at least one asset.");
      return;
    }

    if (publishDate == "Invalid date" || !publishDate) {
      showError("Please select publish date for the loading screen logo.");
      return;
    }
    if (!neverEnding) {
      if (unpublishDate == "Invalid date" || !unpublishDate) {
        showError("Please select unpublish date for the loading screen logo.");
        return;
      } else if (unpublishDate < publishDate) {
        showError("Unpublish date should be after the publish date for the loading screen logo.");
        return;
      }
    }

    const formData = new FormData();
    if (assetId) {
      formData.append("id", assetId);
      formData.append("_method", "PUT");
    }
    if (animationImage.changed && animationImage.data && !animationImage.deleted) {
      formData.append("animation_image", JSON.stringify(animationImage.data));
    }
    if (animationImage.deleted) {
      formData.append("animation_image", "");
    }
    formData.append("publish_date", publishDate);
    formData.append("unpublish_date", neverEnding ? null : unpublishDate);

    const headers = new Headers();
    headers.append("Authorization", "Bearer " + token);
    setSaving(true);

    const endpoint = assetId
      ? `${process.env.REACT_APP_TOKRI_API_URL}/api/app-customization-assets/update`
      : `${process.env.REACT_APP_TOKRI_API_URL}/api/app-customization-assets`;

    fetch(endpoint, {
      method: "POST",
      headers,
      body: formData,
    })
      .then((response) => {
        if (!response.ok) throw new Error();
        return response.json().catch(() => ({}));
      })
      .then((result) => {
        const data = result?.data || result;
        setAssetId(data?.id || assetId);
        setAnimationImage((asset) => ({ ...asset, deleted: false, changed: false }));
        setScheduleChanged(false);
        setSuccessAlert(true);
      })
      .catch(() => showError("Unable to update Loading Screen Logo."))
      .finally(() => setSaving(false));
  };

  const renderScheduleFields = () => (
    <Row className="mt-3">
      <Col lg={4} md={6} sm={6}>
        <div className="mb-3">
          <label>Publish At</label>
          <div className="col-md-12">
            <input
              className="form-control"
              type="datetime-local"
              onChange={(e) => {
                setPublishDate(moment(e.target.value).format("YYYY-MM-DDTHH:mm"));
                setScheduleChanged(true);
              }}
              value={publishDate}
            />
          </div>
        </div>
      </Col>
      <Col lg={1}></Col>
      <Col lg={4} md={6} sm={6}>
        <div className="mb-3">
          <label>Unpublish At</label>
          <div className="col-md-12">
            <input
              className="form-control"
              type="datetime-local"
              onChange={(e) => {
                setUnpublishDate(moment(e.target.value).format("YYYY-MM-DDTHH:mm"));
                setScheduleChanged(true);
              }}
              value={unpublishDate}
              disabled={neverEnding}
            />
          </div>
        </div>
      </Col>
      <Col style={{ paddingTop: 35 }}>
        <div className="form-check form-switch">
          <input
            type="checkbox"
            className="form-check-input checkbox"
            id="checkbox_never_ending_loading_screen_logo"
            checked={neverEnding}
            onChange={() => {
              setNeverEnding(!neverEnding);
              setScheduleChanged(true);
            }}
          />
          <label className="form-check-label" htmlFor="checkbox_never_ending_loading_screen_logo">
            Never-ending
          </label>
        </div>
      </Col>
    </Row>
  );

  if (loading) {
    return <div className="page-content text-center"><CircularProgress /></div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="App Customisation" breadcrumbItem="Loading Screen Logo" />
        <Row>
          <Col lg={12}>
            <Card className="mb-3">
              <CardBody>
                {/* <h5>Loading Screen Logo</h5> */}
                <p className="text-muted">Lottie JSON · less than 150KB · 497 x 120px</p>
                {animationImage.data && !animationImage.deleted && (
                  <div className="d-flex align-items-start mb-3">
                    <div style={{ width: "250px", maxWidth: "100%" }}><LottiePreview animationData={animationImage.data} /></div>
                    <Button color="link" className="text-danger" onClick={() => setAnimationImage({ ...animationInitialState, deleted: true, changed: true })}>
                      <FaRegTrashAlt /> Delete
                    </Button>
                  </div>
                )}
                <Dropzone multiple={false} accept="application/json,.json" onDrop={(files) => files[0] && selectAnimation(files[0])}>
                  {({ getRootProps, getInputProps }) => (
                    <div className="dropzone">
                      <div className="dz-message needsclick" {...getRootProps()}>
                        <input {...getInputProps()} />
                        <FaCloudUploadAlt className="display-4 text-muted mb-3" />
                        <h4>Drop a Lottie JSON file here or click to upload.</h4>
                      </div>
                    </div>
                  )}
                </Dropzone>
              </CardBody>
            </Card>
            <Card className="mb-3">
              <CardBody>
                {/* <h5>Schedule (Loading Screen Logo)</h5> */}
                {renderScheduleFields()}
                <div style={{ marginTop: "1rem" }}>
                  <button
                    disabled={saving}
                    style={{ color: "#ffffff", width: "80px", marginLeft: "10px" }}
                    className={"btn btn-success"}
                    onClick={() => {
                      handleSubmit();
                    }}
                    color="success"
                    type="submit"
                  >
                    {saving ? (
                      <CircularProgress size={15} value={100} color={"white"} />
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      {successAlert && <SweetAlert title="Success!" success onConfirm={() => setSuccessAlert(false)}>Loading Screen Logo updated successfully.</SweetAlert>}
      {errorMessage && <SweetAlert title="Warning!" warning onConfirm={() => setErrorMessage("")}>{errorMessage}</SweetAlert>}
    </div>
  );
};

export default LoadingScreenLogo;
